import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { OfferItemsEditor } from './OfferItemsEditor';
import { RichTextEditor } from '../../../components/RichTextEditor';
import { PROSPECT_TYPES, errMsg } from './crmConstants';

const EMPTY = { name: 'Nuovo template', prospect_type: 'privato', intro: '<p>Ciao {{name}},</p><p>ecco la tua proposta VocalFitness.</p>', items: [{ description: '', qty: 1, unit_price: 0 }], discount_pct: 0, vat_pct: 22, validity_days: 30, terms: 'Prezzi IVA esclusa.' };

const TemplateForm = ({ value, onChange }) => (
  <div className="space-y-3">
    <div className="grid sm:grid-cols-[1fr_160px_120px] gap-2">
      <input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm" data-testid="tpl-name-input" />
      <select value={value.prospect_type} onChange={(e) => onChange({ ...value, prospect_type: e.target.value })} className="px-2 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm" data-testid="tpl-type-select">
        {PROSPECT_TYPES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
      <label className="flex items-center gap-1 text-xs text-slate-400">Validità gg
        <input type="number" value={value.validity_days} onChange={(e) => onChange({ ...value, validity_days: Number(e.target.value) })} className="w-14 px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm" data-testid="tpl-validity-input" />
      </label>
    </div>
    <div>
      <p className="text-[11px] text-slate-500 mb-1">Testo introduttivo. Variabili: {'{{name}} {{company}} {{englishLevel}} {{role}} {{sector}}'}</p>
      <div data-testid="tpl-intro-input"><RichTextEditor key={value.id || 'new'} value={value.intro} onChange={(html) => onChange({ ...value, intro: html })} placeholder="Ciao {{name}}, ecco la tua proposta…" minHeight={90} /></div>
    </div>
    <OfferItemsEditor value={value} onChange={onChange} testIdPrefix="tpl-items" />
    <input value={value.terms} onChange={(e) => onChange({ ...value, terms: e.target.value })} placeholder="Condizioni (pagamento, IVA, note)" className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm" data-testid="tpl-terms-input" />
  </div>
);

export const OfferTemplatesDialog = ({ open, onClose, api, showToast, isAdmin }) => {
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const load = () => api.get('/admin/offer-templates').then((t) => { setList(t); setSel((s) => (s ? t.find((x) => x.id === s.id) || t[0] : t[0]) || null); });
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    try {
      const saved = sel.id ? await api.put(`/admin/offer-templates/${sel.id}`, sel) : await api.post('/admin/offer-templates', sel);
      showToast('success', 'Template salvato'); setSel(saved); load();
    } catch (e) { showToast('error', errMsg(e)); }
  };
  const remove = async () => {
    if (!sel.id || !window.confirm(`Eliminare il template "${sel.name}"?`)) return;
    await api.del(`/admin/offer-templates/${sel.id}`); setSel(null); load();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl bg-slate-800 border-slate-700 text-white max-h-[92vh] overflow-y-auto" data-testid="offer-templates-dialog">
        <DialogHeader>
          <DialogTitle>Template offerte</DialogTitle>
          <DialogDescription className="text-slate-400">Un listino per ogni tipo di prospect. Dal lead generi l'offerta in un clic e la ritocchi prima dell'invio.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-[220px_1fr] gap-4">
          <div className="space-y-1.5">
            {list.map((t) => (
              <button key={t.id} onClick={() => setSel(t)} className={`w-full text-left px-3 py-2 rounded-lg text-sm ${sel?.id === t.id ? 'bg-amber-500 text-slate-950 font-semibold' : 'bg-slate-900 text-slate-200 hover:bg-slate-700'}`} data-testid={`tpl-item-${t.id}`}>
                {t.name}<span className="block text-[10px] uppercase opacity-70">{PROSPECT_TYPES.find((p) => p.id === t.prospect_type)?.label}{t.note ? ' · prezzi di esempio' : ''}</span>
              </button>
            ))}
            {isAdmin && <button onClick={() => setSel({ ...EMPTY })} className="w-full flex items-center gap-1 px-3 py-2 rounded-lg text-sm text-amber-400 border border-dashed border-amber-500/40 hover:bg-amber-500/10" data-testid="tpl-new-btn"><Plus className="w-4 h-4" /> Nuovo template</button>}
          </div>
          {sel ? (
            <div className="space-y-3">
              {sel.note && <p className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">{sel.note}</p>}
              <TemplateForm value={sel} onChange={setSel} />
              {!isAdmin && <p className="text-xs text-slate-400">Solo l'amministratore può modificare i listini.</p>}
              {isAdmin && <div className="flex justify-between">
                <Button variant="ghost" onClick={remove} disabled={!sel.id} className="text-rose-400 hover:bg-rose-500/10" data-testid="tpl-delete-btn"><Trash2 className="w-4 h-4 mr-1" /> Elimina</Button>
                <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="tpl-save-btn"><Save className="w-4 h-4 mr-1" /> Salva template</Button>
              </div>}
            </div>
          ) : <p className="text-slate-500 text-sm">Seleziona un template</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
